import React from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Theme,
  Box,
  Chip,
  Modal,
  IconButton,
  TableSortLabel,
  Typography,
  Link,
} from "@mui/material";
import { SortType } from "../../../../../models/SortType";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { observer } from "mobx-react";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import CloseIcon from "@mui/icons-material/Close";
import { useStores } from "../../../use-stores";
import {
  ProductImage,
  UrgentSourcingProductDetails,
} from "../../../../../models/UrgentSourcing/UrgentSourcing";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

interface UrgentSourcingProductsTableProps {
  data: UrgentSourcingProductDetails[];
  maxHeight?: number;
  minHeight?: number;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy?: string;
  sortType?: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange?: (column: string, sortType: SortType) => void;
  Addproduct?: (id: number) => void;
  handleViewImagesOfProduct?: (images: ProductImage[]) => void;
}

const UrgentSourcingProductsTable = ({
  data,
  maxHeight,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  minHeight = window.innerHeight - 350,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  Addproduct,
  handleViewImagesOfProduct,
}: UrgentSourcingProductsTableProps) => {
  const theme = useTheme<Theme>();
  const { urgentSourcingStore } = useStores();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  return (
    <>
      {data.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight: maxHeight, minHeight: minHeight }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 0,
                      zIndex: 90,
                      minWidth: "80px",
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange?.(
                          "id",
                          sortBy === "id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => <span>{renderSortIcon("id")}</span>}
                      sx={{ fontSize: "12px" }}
                    >
                      Catalog ID
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      left: { md: 85 },
                      zIndex: { md: 80 },
                      boxShadow: {
                        md: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      },
                      maxWidth: "120px",
                      minWidth: "120px",
                      position: "sticky",
                      textAlign: "center",
                      backgroundColor: { md: theme.palette.background.paper },
                    }}
                  >
                    Product ID
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="80px">
                    Request ID
                  </AppTableHeaderCell>

                  <AppTableHeaderCell
                    // sx={{
                    //   left: { md: 80 },
                    //   zIndex: { md: 20 },
                    //   boxShadow: {
                    //     md: "10px 0 20px -12px rgba(115,115,115,0.3);",
                    //   },
                    //   maxWidth: "300px",
                    //   minWidth: "250px",
                    //   position: "sticky",
                    //   textAlign: "center",
                    //   backgroundColor: { md: theme.palette.background.paper },
                    // }}
                  >
                    <TableSortLabel
                      active={sortBy === "product_name"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange?.(
                          "product_name",
                          sortBy === "product_name" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("product_name")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Product
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "timeline"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange?.(
                          "timeline",
                          sortBy === "timeline" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("timeline")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Timeline (Days)
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Price Without Tax
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    50 Quantity Price
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Category
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="80px">GST%</AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Price With GST
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Selling Price
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    HSN Code
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="150px">
                    Description
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Vendor Name
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Vendor Phone No.
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "created_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange?.(
                          "created_date",
                          sortBy === "created_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("created_date")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Created Date
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="150px">
                    Approved By
                  </AppTableHeaderCell>

                  
                </TableRow>
              </TableHead>

              <TableBody>
                {data?.map((item: UrgentSourcingProductDetails) => {
                  const primaryImageUrl = item.images?.[0]?.image_url;
                  const hasImages = Boolean(primaryImageUrl);

                  return (
                    <TableRow key={item.id}>
                      <AppTableRowCell
                        sx={{
                          textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 0,
                      zIndex: 20,
                      minWidth: "120px",
                      backgroundColor: theme.palette.background.paper,                     
                        }}
                      >
                        {item.id}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          left: { md: 85 },
                          zIndex: { md: 20 },
                          boxShadow: {
                            md: "10px 0 20px -12px rgba(115,115,115,0.3);",
                          },
                          maxWidth: "140px",
                          minWidth: "140px",
                          position: "sticky",
                          textAlign: "center",
                          backgroundColor: { md: theme.palette.background.paper },
                        }}
                      >
                        {!item.product_id ? (
                          <Chip
                            label="Add Product"
                            onClick={() => Addproduct?.(item.id!)}
                            variant="outlined"
                            sx={{
                              color: theme.palette.blue.main,
                              borderColor: theme.palette.blue.main,
                            }}
                          />
                        ) : (
                          <Link
                            href={`/products/${item.product_id}`}
                            target="_blank"
                            sx={{
                              color: theme.palette.blue.main,
                              fontWeight: 600,
                              fontSize: "13px",
                              cursor: "pointer",
                              textDecoration: "underline",
                              "&:hover": {
                                color: theme.palette.purple.main,
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {item.product_id}
                          </Link>
                        )}
                      </AppTableRowCell>
                      
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.request_id || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          minWidth: "250px",
                          maxWidth: "300px",
                          fontSize: "12px",
                          textAlign: "left",
                          paddingLeft: "20px",
                          // left: { md: 60 },
                          // position: { xs: "static", md: "sticky" },
                          // zIndex: { md: 15 },
                          
                          backgroundColor: {
                            md: theme.palette.background.paper,
                          },
                          fontWeight: 600,
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        <Box display="flex" alignItems="center">
                          <Box
                            sx={{
                              position: "relative",
                              background: "rgba(242, 242, 242, 1)",
                              padding: 1,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: 2,
                              minHeight: "55px",
                              maxHeight: "55px",
                              borderRadius: "4px",
                            }}
                            onMouseEnter={() =>
                              hasImages &&
                              urgentSourcingStore.setHoveredProductId(item.id)
                            }
                            onMouseLeave={() =>
                              urgentSourcingStore.setHoveredProductId(null)
                            }
                          >
                            {hasImages ? (
                              <img
                                style={{
                                  maxHeight: "38px",
                                  minHeight: "38px",
                                  maxWidth: "38px",
                                  minWidth: "38px",
                                }}
                                src={primaryImageUrl}
                                alt={item.product_name || "-"}
                              />
                            ) : (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.palette.grey[500],
                                  fontSize: "10px",
                                }}
                              >
                                No Image
                              </Typography>
                            )}
                            {urgentSourcingStore.hoveredProductId === item.id &&
                              hasImages && (
                                <Box
                                  className="delete-icon-2"
                                  sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    backgroundColor: "#00000020",
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: "10px",
                                    alignItems: "center",
                                    opacity: 1,
                                    transition: "opacity 0.3s ease-in-out",
                                    zIndex: 1000,
                                  }}
                                >
                                  <VisibilityOutlinedIcon
                                    sx={{
                                      color: "#fff",
                                      cursor: "pointer",
                                      backgroundColor: "#00000090",
                                      borderRadius: "50%",
                                      padding: "4px",
                                      height: 20,
                                      width: 20,
                                    }}
                                    onClick={() =>
                                      handleViewImagesOfProduct?.(
                                        item.images ?? []
                                      )
                                    }
                                  />
                                </Box>
                              )}
                          </Box>
                          <Box sx={{ textAlign: "left" }}>
                            <CustomTypography
                              variant="body2"
                              fontWeight={700}
                              title={item.product_name || "-"}
                            >
                              {item.product_name || "-"}
                            </CustomTypography>
                          </Box>
                        </Box>
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.timeline || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.price_without_tax || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.fifty_quantity_price_without_tax || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.category_name || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.gst === 0 || item.gst ? item.gst : "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.price_with_gst || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.selling_price || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.hsn || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.description ? (
                          <AppButton
                            label="View Desc"
                            variant="transparentPrimary"
                            customStyles={{
                              fontSize: "12px",
                              padding: "4px 8px",
                              minWidth: "auto",
                              color: theme.palette.blue.main,
                            }}
                            onClick={() => {
                              urgentSourcingStore.managePopup(
                                "openDescriptionModal",
                                "open",
                                {
                                  description: item.description || "",
                                  descriptionType: "product",
                                }
                              );
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.vendor_name || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.vendor_phone_number || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {formatDate(item.created_date) || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.added_by_name || "-"}
                      </AppTableRowCell>

                     
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      )}
      <Modal
        open={urgentSourcingStore.popupState.openDescriptionModal}
        onClose={() =>
          urgentSourcingStore.managePopup("openDescriptionModal", "close")
        }
        aria-labelledby="description-modal-title"
        aria-describedby="description-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 800,
            maxHeight: "80vh",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <CustomTypography variant="heading5" fontWeight={600}>
              {urgentSourcingStore.selectedDescriptionType === "product"
                ? "Product Description"
                : urgentSourcingStore.selectedDescriptionType === "cancel"
                ? "Cancel Reason Description"
                : "Description"}
            </CustomTypography>
            <IconButton
              onClick={() =>
                urgentSourcingStore.managePopup("openDescriptionModal", "close")
              }
              sx={{
                color: theme.palette.grey[500],
                "&:hover": {
                  color: theme.palette.grey[700],
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              p: 2,
              borderRadius: 1,
              border: `1px solid ${theme.palette.grey[200]}`,
              maxHeight: "60vh",
              overflow: "auto",
            }}
          >
            <Box
              dangerouslySetInnerHTML={{
                __html:
                  urgentSourcingStore.selectedDescription ||
                  "No description available",
              }}
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "14px",
                lineHeight: "1.6",
                "& h1, & h2, & h3, & h4, & h5, & h6": {
                  marginTop: "16px",
                  marginBottom: "8px",
                },
                "& p": {
                  marginBottom: "12px",
                },
                "& ul, & ol": {
                  marginBottom: "12px",
                  paddingLeft: "20px",
                },
                "& li": {
                  marginBottom: "4px",
                },
              }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default observer(UrgentSourcingProductsTable);
